import { useCallback, useEffect, useRef, useState } from "react";
import Editor, { type EditorHandle } from "./Editor";
import { Icon } from "./Icon";
import { noteExcerpt } from "../lib/excerpt";
import { navigate, useAppViewport } from "./motion";
import {
  api,
  authenticate,
  type NoteDetail,
  type NoteSummary,
  type User,
} from "./api";

const LIFF_ID = "2011751815-AGWLCx5K";
const DEMO_MODE =
  import.meta.env.DEV && new URLSearchParams(location.search).has("demo");
type AppState =
  | { kind: "loading"; message: string }
  | { kind: "error"; message: string }
  | { kind: "ready"; user: User };
type NoteTemplate = {
  id: string;
  title: string;
  body: string;
  description: string;
  icon: "note" | "check" | "edit";
};
const noteTemplates: NoteTemplate[] = [
  {
    id: "blank",
    title: "空のメモ",
    body: "",
    description: "好きなことを自由に書く",
    icon: "note",
  },
  {
    id: "shopping",
    title: "買い物リスト",
    body: "- [ ] &#8203;\n- [ ] &#8203;\n- [ ] &#8203;",
    description: "品物を追加してチェック",
    icon: "check",
  },
  {
    id: "todo",
    title: "やること",
    body: "- [ ] &#8203;\n- [ ] &#8203;\n- [ ] &#8203;",
    description: "やることと済んだことを整理",
    icon: "check",
  },
  {
    id: "plan",
    title: "予定メモ",
    body: "日時：\n場所：\nメモ：",
    description: "日時や場所を先にメモ",
    icon: "edit",
  },
];

export default function App() {
  useAppViewport();
  const editorRef = useRef<EditorHandle>(null);
  const openingLock = useRef(false);
  useEffect(() => {
    const preventFileNavigation = (event: DragEvent) => {
      if (!Array.from(event.dataTransfer?.types ?? []).includes("Files")) return;
      event.preventDefault();
      if (event.dataTransfer && !(event.target instanceof Element && event.target.closest(".editor-shell"))) {
        event.dataTransfer.dropEffect = "none";
      }
    };
    window.addEventListener("dragover", preventFileNavigation);
    window.addEventListener("drop", preventFileNavigation);
    return () => {
      window.removeEventListener("dragover", preventFileNavigation);
      window.removeEventListener("drop", preventFileNavigation);
    };
  }, []);
  const [state, setState] = useState<AppState>({
    kind: "loading",
    message: "LINEと接続しています",
  });
  const [notes, setNotes] = useState<NoteSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [initialNote, setInitialNote] = useState<NoteDetail | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [listActionsId, setListActionsId] = useState<string | null>(null);
  const [templatePicker, setTemplatePicker] = useState(false);
  const held = useRef(false);
  const holdTimer = useRef<number | null>(null);
  const holdStart = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (templatePicker)
      document.querySelector<HTMLButtonElement>(".template-option")?.focus();
  }, [templatePicker]);
  const armListHold = (id: string, x: number, y: number) => {
    held.current = false;
    holdStart.current = { x, y };
    if (holdTimer.current) window.clearTimeout(holdTimer.current);
    holdTimer.current = window.setTimeout(() => {
      held.current = true;
      setListActionsId(id);
    }, 480);
  };
  const moveListHold = (x: number, y: number) => {
    const start = holdStart.current;
    if (
      start &&
      (Math.abs(x - start.x) > 18 || Math.abs(y - start.y) > 18)
    ) {
      if (holdTimer.current) window.clearTimeout(holdTimer.current);
      holdStart.current = null;
    }
  };
  const cancelListHold = () => {
    if (holdTimer.current) window.clearTimeout(holdTimer.current);
    holdStart.current = null;
  };

  useEffect(() => () => {
    if (holdTimer.current) window.clearTimeout(holdTimer.current);
  }, []);

  const loadNotes = useCallback(async () => {
    if (DEMO_MODE) {
      const { demoNotes } = await import("./demo");
      setNotes(demoNotes());
      setLoaded(true);
      return;
    }
    const result = await api<{ notes: NoteSummary[] }>("/api/notes");
    setNotes(result.notes);
    setLoaded(true);
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        if (DEMO_MODE) {
          const { installDemo } = await import("./demo");
          installDemo();
          const demoUser = {
            id: "demo",
            displayName: "つばさ",
            pictureUrl: null,
          };
          if (active) setState({ kind: "ready", user: demoUser });
          return;
        }
        const { default: liff } = await import("@line/liff");
        await liff.init({ liffId: LIFF_ID });
        if (!liff.isLoggedIn()) {
          liff.login({ redirectUri: location.href });
          return;
        }
        const lineToken = liff.getAccessToken();
        if (!lineToken)
          throw new Error("LINEのアクセストークンを取得できませんでした");
        const user = await authenticate(lineToken);
        if (!active) return;
        setState({ kind: "ready", user });

        const joinToken = new URLSearchParams(location.search).get("join");
        if (joinToken) {
          const claimed = await api<{ noteId: string }>("/api/share/claim", {
            method: "POST",
            body: JSON.stringify({ token: joinToken }),
          });
          history.replaceState(null, "", location.pathname);
          setSelectedId(claimed.noteId);
        }
      } catch (error) {
        if (active) setState({ kind: "error", message: errorMessage(error) });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (state.kind !== "ready") return;
    void loadNotes().catch((error) => setListError(errorMessage(error)));
  }, [loadNotes, state.kind]);

  const updateSummary = useCallback((note: NoteDetail) => {
    setNotes(items => items.map(item => item.id === note.id
      ? { ...item, title: note.title, excerpt: noteExcerpt(note.body), revision: note.revision, updatedAt: note.updatedAt }
      : item));
  }, []);

  const createNote = async (template: NoteTemplate) => {
    if (openingLock.current || busy) return;
    openingLock.current = true;
    setBusy(true);
    try {
      if (editorRef.current && !(await editorRef.current.prepareToLeave())) return;
      setTemplatePicker(false);
      const note = await api<NoteDetail>("/api/notes", {
        method: "POST",
        body: JSON.stringify({ title: template.title, body: template.body }),
      });
      const detail = await api<NoteDetail>(`/api/notes/${note.id}`);
      void loadNotes().catch((e) => setListError(errorMessage(e)));
      navigate("forward", () => {
        setInitialNote(detail);
        setCreatedId(note.id);
        setSelectedId(note.id);
      });
    } catch (error) {
      setListError(errorMessage(error));
    } finally {
      openingLock.current = false;
      setBusy(false);
    }
  };

  const openNote = async (id: string) => {
    if (openingLock.current || busy || selectedId === id) return;
    openingLock.current = true;
    setOpeningId(id);
    setListError(null);
    try {
      if (editorRef.current && !(await editorRef.current.prepareToLeave())) return;
      const detail = await api<NoteDetail>(`/api/notes/${id}`);
      void loadNotes().catch((e) => setListError(errorMessage(e)));
      navigate("forward", () => {
        setInitialNote(detail);
        setSelectedId(id);
      });
    } catch (e) {
      setListError(errorMessage(e));
    } finally {
      openingLock.current = false;
      setOpeningId(null);
    }
  };
  const ownedNoteCount = notes.filter((note) => note.role === "owner").length;
  const noteLimitReached = ownedNoteCount >= 3;

  if (state.kind === "loading")
    return <StatusScreen mark="束" title="まとメモ" message={state.message} />;
  if (state.kind === "error") {
    return (
      <StatusScreen
        mark="!"
        title="開けませんでした"
        message={state.message}
        action={() => location.reload()}
      />
    );
  }


  return (
    <div className={`workspace ${selectedId ? "has-note" : ""}`}>
    <main className="app-shell" onPointerDownCapture={(e) => {
      if (!(e.target instanceof Element && e.target.closest(".list-note-actions"))) setListActionsId(null);
    }}
      onKeyDown={(e) => { if (e.key === "Escape") setListActionsId(null); }}>
      <header className="desktop-home-heading"><Icon name="note" /><h1>まとメモ</h1></header>
      <div className="screen-scroll home-scroll">
        {listError && (
          <div className="notice error" role="alert">
            {listError}
            <button
              onClick={() => {
                setListError(null);
                void loadNotes().catch((e) => setListError(errorMessage(e)));
              }}
            >
              もう一度試す
            </button>
          </div>
        )}
        <section className="note-index" aria-label="メモ一覧">
          {!loaded ? (
            <p className="list-loading" role="status">
              メモを読み込み中…
            </p>
          ) : notes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-symbol">
                <Icon name="note" />
              </div>
              <h2>ひとつ、書いてみよう。</h2>
              <p>今日の予定も、買うものも。 写真といっしょに、ここに。</p>
            </div>
          ) : (
            notes.map((note) => (
              <div
                className={`note-row ${selectedId === note.id ? "is-selected" : ""}`}
                key={note.id}
                onPointerDown={(event) => {
                  if (event.button !== 0) return;
                  held.current = false;
                  if (note.role === "owner") armListHold(note.id, event.clientX, event.clientY);
                }}
                onPointerMove={(event) => moveListHold(event.clientX, event.clientY)}
                onPointerUp={cancelListHold}
                onPointerCancel={cancelListHold}
                onContextMenu={(event) => {
                  event.preventDefault();
                  cancelListHold();
                  if (note.role === "owner") setListActionsId(note.id);
                }}
              >
                <button
                  className="note-row-open"
                  disabled={!!openingId || busy}
                  aria-current={selectedId === note.id ? "page" : undefined}
                  aria-busy={openingId === note.id}
                  onClick={() => {
                    if (held.current) {
                      held.current = false;
                      return;
                    }
                    setListActionsId(null);
                    void openNote(note.id);
                  }}
                >
                  <span className="note-copy">
                    <strong>{note.title}</strong>
                    <span>{note.excerpt || "本文はまだありません"}</span>
                  </span>
                  {note.role !== "owner" && <span className="note-time">共有されたメモ</span>}
                </button>
                {note.role === "owner" && <button className="desktop-note-menu" aria-label={`${note.title}の操作`}
                  onPointerDown={(e) => e.stopPropagation()} onClick={() => setListActionsId(listActionsId === note.id ? null : note.id)}>
                  <Icon name="more" />
                </button>}
                {listActionsId === note.id && note.role === "owner" && (
                  <div className="list-note-actions" onPointerDown={(e) => e.stopPropagation()}>
                    <button
                      className="delete-note"
                      onClick={async () => {
                        if (
                          !window.confirm(
                            "このメモと添付画像を削除します。削除しますか？",
                          )
                        )
                          return;
                        try {
                          if (note.id === selectedId && editorRef.current && !(await editorRef.current.prepareToLeave())) return;
                          await api<void>(`/api/notes/${note.id}`, {
                            method: "DELETE",
                          });
                          setNotes((items) =>
                            items.filter((item) => item.id !== note.id),
                          );
                          setListActionsId(null);
                          if (note.id === selectedId) {
                            setSelectedId(null);
                            setInitialNote(null);
                            setCreatedId(null);
                          }
                        } catch (error) {
                          setListError(errorMessage(error));
                        }
                      }}
                    >
                      削除
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </section>

      </div>
      <button
        className="create-note-button"
        aria-label={busy ? "作成中" : noteLimitReached ? "ノートは3件までです" : "メモを書く"}
        title={noteLimitReached ? "ノートは3件までです" : "メモを書く"}
        onClick={() => setTemplatePicker(true)}
        disabled={busy || !!openingId || !loaded || noteLimitReached}
      >
        <Icon name="plus" /><span className="desktop-label">メモを書く</span>
      </button>
    </main>
    {selectedId ? (
      <Editor
        ref={editorRef}
        onSaved={updateSummary}
        noteId={selectedId}
        key={selectedId}
        startEditing={createdId === selectedId}
        initialNote={initialNote?.id === selectedId ? initialNote : undefined}
        onBack={() => {
          navigate("back", () => {
            setSelectedId(null);
            setCreatedId(null);
            setInitialNote(null);
          });
          void loadNotes().catch((e) => setListError(errorMessage(e)));
        }}
      />
    ) : (
      <section className="desktop-welcome">
        <Icon name="note" /><h2>メモを選んで、続きを。</h2>
        <p>左の一覧からメモを開けます。</p>
        <span>画像のドラッグ＆ドロップ・貼り付けにも対応</span>
      </section>
    )}
      {templatePicker && (
        <div
          className="sheet-backdrop"
          onClick={() => setTemplatePicker(false)}
        >
          <section
            className="template-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="template-title"
            onKeyDown={(event) => {
              if (event.key === "Escape") setTemplatePicker(false);
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="template-title">どんなメモにする？</h2>
            <p>使い方に合わせて、書き出しを選べます。</p>
            <div className="template-options">
              {noteTemplates.map((template) => (
                <button
                  className="template-option"
                  key={template.id}
                  disabled={busy}
                  onClick={() => void createNote(template)}
                >
                  <Icon name={template.icon} />
                  <strong>{template.title}</strong>
                  <span>{template.description}</span>
                </button>
              ))}
            </div>
            <button
              className="quiet-button"
              onClick={() => setTemplatePicker(false)}
            >
              閉じる
            </button>
          </section>
        </div>
      )}
    </div>
  );
}

function StatusScreen({
  mark,
  title,
  message,
  action,
}: {
  mark: string;
  title: string;
  message: string;
  action?: () => void;
}) {
  return (
    <main className="status-screen">
      <div className="status-mark">{mark}</div>
      <h1>{title}</h1>
      <p>{message}</p>
      {action && (
        <button className="primary-button" onClick={action}>
          もう一度試す
        </button>
      )}
    </main>
  );
}

function errorMessage(value: unknown): string {
  return value instanceof Error ? value.message : "処理に失敗しました";
}
